import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { MessageSquare, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

// Type definition for chat message notification data
interface ChatMessageNotification {
  id: string;
  title: string;
  message: string;
  type: 'chat_message';
  metadata: {
    sessionId: string;
    senderId: string;
    senderName: string;
    messagePreview: string;
    messageType: string;
  };
  createdAt: string;
}

interface ChatMessageToastProps {
  notification: ChatMessageNotification;
  onClick: () => void;
  className?: string;
}

// Helper function to get user initials
const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(part => part.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

// Helper function to truncate message
const truncateMessage = (message: string, maxLength: number = 50): string => {
  if (message.length <= maxLength) return message;
  return message.slice(0, maxLength).trim() + '...';
};

export const ChatMessageToast: React.FC<ChatMessageToastProps> = ({
  notification,
  onClick,
  className
}) => {
  const { metadata } = notification;

  return (
    <div
      onClick={onClick}
      className={cn(
        "group relative w-full cursor-pointer overflow-hidden rounded-lg border bg-white p-4 shadow-lg transition-all duration-300 hover:shadow-xl hover:scale-[1.02] hover:border-blue-200",
        "data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)]",
        "data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none",
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out",
        "data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full",
        "data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full",
        className
      )}
    >
      {/* Gradient Background Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50/50 to-purple-50/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

      {/* Content */}
      <div className="relative flex items-start space-x-3">
        {/* Avatar with Online Indicator */}
        <div className="relative flex-shrink-0">
          <Avatar className="h-12 w-12 ring-2 ring-white shadow-md group-hover:ring-blue-100 transition-all duration-300">
            <AvatarImage
              src={undefined} // No profile picture URL available in notification
              alt={metadata.senderName}
              className="object-cover"
            />
            <AvatarFallback className="bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-500 text-white font-semibold text-sm">
              {getInitials(metadata.senderName)}
            </AvatarFallback>
          </Avatar>
          {/* Online Status Indicator */}
          <div className="absolute -bottom-0.5 -right-0.5 h-4 w-4 bg-green-500 border-2 border-white rounded-full">
            <div className="absolute inset-0 bg-green-400 rounded-full animate-ping opacity-75" />
          </div>
        </div>

        {/* Message Content */}
        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center space-x-2">
              <h3 className="font-semibold text-gray-900 text-sm group-hover:text-blue-700 transition-colors duration-200">
                {metadata.senderName}
              </h3>
              <div className="flex items-center space-x-1 text-xs text-gray-500">
                <MessageSquare className="h-3 w-3" />
                <span>New message</span>
              </div>
            </div>
            {/* <div className="flex items-center space-x-1 text-xs text-gray-400 group-hover:text-gray-600 transition-colors duration-200">
              <span className="font-medium">{timeAgo}</span>
            </div> */}
          </div>

          {/* Message Preview */}
          <div className="relative mb-2">
            <p className="text-sm text-gray-700 leading-relaxed group-hover:text-gray-900 transition-colors duration-200">
              {truncateMessage(metadata.messagePreview)}
            </p>
          </div>

          {/* Action Indicator */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-1 text-xs text-blue-600 group-hover:text-blue-700 transition-colors duration-200">
              <span className="font-medium">Click to respond</span>
              <ArrowRight className="h-3 w-3 group-hover:translate-x-0.5 transition-transform duration-200" />
            </div>

            {/* Message Type Badge */}
            {/* <div className="px-2 py-1 bg-blue-50 group-hover:bg-blue-100 text-blue-700 text-xs font-medium rounded-full transition-colors duration-200 border border-blue-200">
              {metadata.messageType === 'text' ? 'Text' : metadata.messageType}
            </div> */}
          </div>
        </div>
      </div>

      {/* Hover Border Effect */}
      <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-200 rounded-lg transition-colors duration-300 pointer-events-none" />
    </div>
  );
};

export type { ChatMessageNotification };